import React, { useEffect, useState } from 'react';
import { getRequest, postRequest, showMessage } from '../../../config';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

const S = {
    wrap: { padding: '1.5rem', background: '#f5f6fa', minHeight: 120 },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e4e8f5', boxShadow: '0 2px 8px rgba(74,103,255,0.06)', overflow: 'hidden' },
    cardHead: { padding: '18px 24px 14px', borderBottom: '1px solid #f0f2ff' },
    title: { fontSize: '1.05rem', fontWeight: 700, color: '#1a1d2e', margin: 0 },
    subtitle: { fontSize: '0.8rem', color: '#7c83a0', marginTop: 4 },
    tableWrap: { overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', width: '100%', WebkitOverflowScrolling: 'touch' },
    thead: { background: '#f8f9ff' },
    th: { padding: '10px 14px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'left', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    thCenter: { padding: '10px 14px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'center', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    modRow: { background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)', cursor: 'pointer', borderTop: '2px solid rgb(36, 62, 207)', userSelect: 'none' },
    modTd: { padding: '10px 16px' },
    modAccent: { width: 3, height: 34, background: '#4A67FF', borderRadius: 2, flexShrink: 0 },
    modName: { fontSize: '0.88rem', fontWeight: 700, color: '#1a1d2e' },
    modKey: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, flexShrink: 0 },
    modMeta: { fontSize: '0.72rem', color: '#9aa0b8' },
    secRow: { background: '#fff' },
    sfRow: { background: '#fafbff' },
    secTd: { padding: '10px 12px 10px 32px', borderBottom: '1px solid #d0d5e8' },
    sfTd: { padding: '8px 12px 8px 52px', borderBottom: '1px solid #eaecf5' },
    secNo: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0 },
    sfNo: { fontSize: '0.65rem', fontWeight: 600, color: '#7c83a0', background: '#f5f6fa', padding: '2px 6px', borderRadius: 8, whiteSpace: 'nowrap', flexShrink: 0 },
    secName: { fontSize: '0.82rem', color: '#374151' },
    sfName: { fontSize: '0.78rem', color: '#6b7280' },
    fieldTd: { padding: '10px 14px', verticalAlign: 'top', borderLeft: '1px solid #eaecf5', borderBottom: '1px solid #d0d5e8' },
    sfFieldTd: { padding: '8px 14px', verticalAlign: 'top', borderLeft: '1px solid #eaecf5', borderBottom: '1px solid #eaecf5' },
    statusTd: { padding: '10px 14px', verticalAlign: 'middle', textAlign: 'center', borderLeft: '1px solid #eaecf5', borderBottom: '1px solid #d0d5e8' },
    sfStatusTd: { padding: '8px 14px', verticalAlign: 'middle', textAlign: 'center', borderLeft: '1px solid #eaecf5', borderBottom: '1px solid #eaecf5' },
    savingLabel: { fontSize: '0.68rem', color: '#9aa0b8', marginTop: 4 },
    textInput: {
        width: '100%',
        borderRadius: 6,
        border: '1px solid #d0d5e8',
        padding: '6px 10px',
        fontSize: '0.8rem',
        color: '#374151',
        outline: 'none',
        fontFamily: 'inherit',
        background: '#fff',
        boxSizing: 'border-box',
    },
};

const SECTION_COL_PX = 260;
const STATUS_COL_PX = 80;
const QTV_COL_PX = 200;
const PROMPT_COL_PX = 480;

// Key for section-level entries
const mkKey = (moduleId, sectionNo) => `${moduleId}-${sectionNo}`;
// Key for subfolder-level entries
const mkSfKey = (moduleId, sectionNo, sfKey) => `${moduleId}-${sectionNo}-sf-${sfKey}`;

// Returns sorted subfolder entries from a subFolders object
const getSortedSubFolders = (subFolders) => {
    if (!subFolders || typeof subFolders !== 'object') return [];
    return Object.keys(subFolders)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(key => ({ sfKey: key, ...subFolders[key] }));
};

// Checkbox label component (shared between sections and subfolders)
const StatusCheckbox = ({ checked, onChange, disabled }) => (
    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11, color: checked ? '#09B255' : '#9aa0b8', fontWeight: 600, userSelect: 'none' }}>
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            style={{ width: 14, height: 14, accentColor: '#4A67FF', cursor: 'pointer' }}
        />
        {checked ? 'Active' : 'Inactive'}
    </label>
);

const TempAnalyser = () => {
    const [tempModules, setTempModules] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({}); // { [key]: { prompt, queryToVector, isActive } }
    const [saving, setSaving] = useState({});

    const fetchModules = async () => {
        setLoading(true);
        try {
            const res = await getRequest('/modules/toolbox/temp-modules', 'not-showMessage');
            if (res?.code === 200) {
                const modules = res.body || [];
                setTempModules(modules);
                const initial = {};
                modules.forEach(mod => {
                    (mod.sections || []).forEach(sec => {
                        initial[mkKey(mod._id, sec.sectionNo)] = {
                            prompt: sec.analyserPrompt || '',
                            queryToVector: sec.analyserQueryToVector || '',
                            isActive: sec.analyserIsActive || false,
                        };
                        // Init subfolder state
                        getSortedSubFolders(sec.subFolders).forEach(({ sfKey }) => {
                            const sfCfg = (sec.analyserSubFolderConfig || {})[sfKey] || {};
                            initial[mkSfKey(mod._id, sec.sectionNo, sfKey)] = {
                                prompt: sfCfg.prompt || '',
                                queryToVector: sfCfg.queryToVector || '',
                                isActive: sfCfg.isActive || false,
                            };
                        });
                    });
                });
                setData(initial);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchModules(); }, []);

    const toggleAccordion = (moduleId) =>
        setOpenAccordions(prev => ({ [moduleId]: !prev[moduleId] }));

    const handleField = (key, field, value) =>
        setData(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

    // Get the original values for a section from tempModules (for dirty check)
    const getOriginalSection = (moduleId, sectionNo) => {
        const mod = tempModules.find(m => String(m._id) === String(moduleId));
        const sec = mod?.sections?.find(s => s.sectionNo === sectionNo);
        return {
            prompt: sec?.analyserPrompt || '',
            queryToVector: sec?.analyserQueryToVector || '',
            isActive: sec?.analyserIsActive || false,
        };
    };

    // Get the original values for a subfolder
    const getOriginalSubFolder = (moduleId, sectionNo, sfKey) => {
        const mod = tempModules.find(m => String(m._id) === String(moduleId));
        const sec = mod?.sections?.find(s => s.sectionNo === sectionNo);
        const sfCfg = (sec?.analyserSubFolderConfig || {})[sfKey] || {};
        return { prompt: sfCfg.prompt || '', queryToVector: sfCfg.queryToVector || '', isActive: sfCfg.isActive || false };
    };

    const saveSection = async (moduleId, sectionNo, overrideData) => {
        const key = mkKey(moduleId, sectionNo);
        const current = overrideData || data[key] || {};
        const orig = getOriginalSection(moduleId, sectionNo);
        if (current.prompt === orig.prompt && current.queryToVector === orig.queryToVector && current.isActive === orig.isActive) return;

        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await postRequest('/modules/toolbox/save-section-prompt', {
                moduleId, sectionNo,
                prompt: current.prompt || '',
                queryToVector: current.queryToVector || '',
                isActive: current.isActive || false,
            });
            if (res?.code === 200) {
                showMessage('Saved.');
                setTempModules(prev => prev.map(m => {
                    if (String(m._id) !== String(moduleId)) return m;
                    return { ...m, sections: m.sections.map(s => s.sectionNo === sectionNo ? { ...s, analyserPrompt: current.prompt || '', analyserQueryToVector: current.queryToVector || '', analyserIsActive: current.isActive || false } : s) };
                }));
            } else showMessage(res?.message || 'Failed to save.');
        } catch (err) { console.error(err); }
        finally { setSaving(prev => ({ ...prev, [key]: false })); }
    };

    const saveSubFolder = async (moduleId, sectionNo, sfKey, overrideData) => {
        const key = mkSfKey(moduleId, sectionNo, sfKey);
        const current = overrideData || data[key] || {};
        const orig = getOriginalSubFolder(moduleId, sectionNo, sfKey);
        if (current.prompt === orig.prompt && current.queryToVector === orig.queryToVector && current.isActive === orig.isActive) return;

        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await postRequest('/modules/toolbox/save-subfolder-prompt', {
                moduleId, sectionNo, subFolderKey: sfKey,
                prompt: current.prompt || '',
                queryToVector: current.queryToVector || '',
                isActive: current.isActive || false,
            });
            if (res?.code === 200) {
                showMessage('Saved.');
                setTempModules(prev => prev.map(m => {
                    if (String(m._id) !== String(moduleId)) return m;
                    return {
                        ...m, sections: m.sections.map(s => {
                            if (s.sectionNo !== sectionNo) return s;
                            const prevCfg = s.analyserSubFolderConfig || {};
                            return { ...s, analyserSubFolderConfig: { ...prevCfg, [sfKey]: { prompt: current.prompt || '', queryToVector: current.queryToVector || '', isActive: current.isActive || false } } };
                        })
                    };
                }));
            } else showMessage(res?.message || 'Failed to save.');
        } catch (err) { console.error(err); }
        finally { setSaving(prev => ({ ...prev, [key]: false })); }
    };

    const handleSectionCheckbox = (moduleId, sectionNo, checked) => {
        const key = mkKey(moduleId, sectionNo);
        const updated = { ...data[key], isActive: checked };
        handleField(key, 'isActive', checked);
        saveSection(moduleId, sectionNo, updated);
    };

    const handleSfCheckbox = (moduleId, sectionNo, sfKey, checked) => {
        const key = mkSfKey(moduleId, sectionNo, sfKey);
        const updated = { ...data[key], isActive: checked };
        handleField(key, 'isActive', checked);
        saveSubFolder(moduleId, sectionNo, sfKey, updated);
    };

    const renderFields = (key, onBlur, isSaving, tdStyle, statusTdStyle) => {
        const d = data[key] || { prompt: '', queryToVector: '', isActive: false };
        return (
            <>
                <td style={statusTdStyle}>
                    <StatusCheckbox
                        checked={d.isActive}
                        onChange={e => {
                            const updated = { ...d, isActive: e.target.checked };
                            handleField(key, 'isActive', e.target.checked);
                            onBlur(updated);
                        }}
                        disabled={isSaving}
                    />
                </td>
                <td style={tdStyle}>
                    <textarea
                        value={d.queryToVector}
                        onChange={e => handleField(key, 'queryToVector', e.target.value)}
                        onBlur={() => onBlur()}
                        disabled={isSaving}
                        placeholder="Enter query to vector…"
                        rows={3}
                        style={{ ...S.textInput, resize: 'vertical', background: isSaving ? '#f8f9ff' : '#fff' }}
                    />
                    {isSaving && <div style={S.savingLabel}>Saving…</div>}
                </td>
                <td style={tdStyle}>
                    <textarea
                        value={d.prompt}
                        onChange={e => handleField(key, 'prompt', e.target.value)}
                        onBlur={() => onBlur()}
                        disabled={isSaving}
                        placeholder="Enter prompt…"
                        rows={3}
                        style={{ ...S.textInput, resize: 'vertical', background: isSaving ? '#f8f9ff' : '#fff' }}
                    />
                </td>
            </>
        );
    };

    if (loading) return <div className="df jcc aic p2rem" style={{ color: '#7c83a0' }}>Loading…</div>;

    return (
        <div style={S.wrap}>
            <div style={S.card}>
                <div style={S.cardHead}>
                    <p style={S.title}>Temp Analyser</p>
                    <p style={S.subtitle}>Configure analyser prompts per section and subfolder for each module.</p>
                </div>
                <div style={S.tableWrap}>
                    <table style={{ width: SECTION_COL_PX + STATUS_COL_PX + QTV_COL_PX + PROMPT_COL_PX, minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                        <colgroup>
                            <col style={{ width: SECTION_COL_PX }} />
                            <col style={{ width: STATUS_COL_PX }} />
                            <col style={{ width: QTV_COL_PX }} />
                            <col style={{ width: PROMPT_COL_PX }} />
                        </colgroup>
                        <thead style={S.thead}>
                            <tr>
                                <th style={{ ...S.th, position: 'sticky', left: 0, top: 0, zIndex: 4, backgroundColor: '#f8f9ff', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.12)' }}>Section</th>
                                <th style={{ ...S.thCenter, position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9ff' }}>Status</th>
                                <th style={{ ...S.th, position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9ff' }}>Query to Vector</th>
                                <th style={{ ...S.th, position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9ff' }}>Prompt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tempModules.map(mod => {
                                const isOpen = !!openAccordions[mod._id];
                                return (
                                    <React.Fragment key={mod._id}>
                                        {/* Module accordion row */}
                                        <tr
                                            style={S.modRow}
                                            onClick={() => toggleAccordion(mod._id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleAccordion(mod._id)}
                                        >
                                            <td style={{ ...S.modTd, position: 'sticky', left: 0, zIndex: 2, background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.08)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={S.modAccent} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={S.modName}>{mod.moduleName}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                                            {mod.moduleKey && <span style={S.modKey}>{mod.moduleKey}</span>}
                                                            <span style={S.modMeta}>{mod.sections?.length || 0} section{mod.sections?.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                    {isOpen ? <KeyboardArrowUp style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} /> : <KeyboardArrowDown style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />}
                                                </div>
                                            </td>
                                            <td style={{ ...S.modTd, borderLeft: '1px solid #e0e4f0' }} onClick={e => e.stopPropagation()} />
                                            <td style={{ ...S.modTd, borderLeft: '1px solid #e0e4f0' }} onClick={e => e.stopPropagation()} />
                                            <td style={{ ...S.modTd, borderLeft: '1px solid #e0e4f0' }} onClick={e => e.stopPropagation()} />
                                        </tr>

                                        {isOpen && (mod.sections || []).map(sec => {
                                            const secKey = mkKey(mod._id, sec.sectionNo);
                                            const isSavingSec = !!saving[secKey];
                                            const subFolders = getSortedSubFolders(sec.subFolders);

                                            return (
                                                <React.Fragment key={secKey}>
                                                    {/* Section row */}
                                                    <tr style={S.secRow}>
                                                        <td style={{ ...S.secTd, position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fff', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.08)' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span style={S.secNo}>{sec.sectionNo}</span>
                                                                <span style={S.secName}>{sec.sectionName}</span>
                                                            </div>
                                                        </td>
                                                        {renderFields(
                                                            secKey,
                                                            (override) => saveSection(mod._id, sec.sectionNo, override),
                                                            isSavingSec,
                                                            S.fieldTd,
                                                            S.statusTd,
                                                        )}
                                                    </tr>

                                                    {/* Subfolder rows */}
                                                    {subFolders.map(({ sfKey, name, no }) => {
                                                        const sfStateKey = mkSfKey(mod._id, sec.sectionNo, sfKey);
                                                        const isSavingSf = !!saving[sfStateKey];
                                                        return (
                                                            <tr key={sfStateKey} style={S.sfRow}>
                                                                <td style={{ ...S.sfTd, position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fafbff', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.05)' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <span style={{ color: '#d0d5e8', fontSize: 12, flexShrink: 0 }}>└</span>
                                                                        {no && <span style={S.sfNo}>{no}</span>}
                                                                        <span style={S.sfName}>{name}</span>
                                                                    </div>
                                                                </td>
                                                                {renderFields(
                                                                    sfStateKey,
                                                                    (override) => saveSubFolder(mod._id, sec.sectionNo, sfKey, override),
                                                                    isSavingSf,
                                                                    S.sfFieldTd,
                                                                    S.sfStatusTd,
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TempAnalyser;
